import type { VehicleSummaryTO, VehicleTO } from '@control-center/control-center-client-public-vehicles-ts';
import {
    AdsStateField,
    ConnectivityStatusField,
    DeviationField,
    DriverModeField,
    DrivingStateField,
    MrmStateField,
    PositionField,
    SpeedMsField,
    VehicleSubtype as WireVehicleSubtype,
    VehicleType as WireVehicleType,
} from '@control-center/control-center-client-public-vehicles-ts/dist/schemas';
import { z } from 'zod';

import type { MonitoredField } from '../monitored';
import type {
    AdsStateFE,
    ConnectivityDetailsFE,
    DeviationFE,
    DeviationSeverityFE,
    DeviationStateFE,
    DriverModeFE,
    DrivingStateFE,
    MrmDetailsFE,
    MrmStateFE,
    VehicleFE,
    VehicleOwnerFE,
    VehicleSubtypeFE,
    VehicleSummaryFE,
    VehicleTypeFE,
} from '../vehicle';
import {
    fromEnum,
    fromPlainEnum,
    makeSanitizeWire,
    safeField,
    safeOptional,
    serializeMonitoredField,
    toDate,
    toGeoPosition,
    toMonitoredField,
} from './restMapping';

export type WireVehicle = VehicleTO;
export type WireVehicleSummary = VehicleSummaryTO;

const label = (field: string) => `vehicleFromRestV2.${field}`;

const VEHICLE_DATE_KEYS = new Set([
    'updated_at',
    'sent_at',
    'raised_at',
    'latest_vehicle_update',
    'timestamp',
    'started_at',
    'resolved_at',
]);

const sanitizeWire = makeSanitizeWire(VEHICLE_DATE_KEYS);

export const WireVehicleSchema = z.preprocess(
    sanitizeWire,
    z.object({
        vin: z.string().min(1),
        name: safeOptional(z.string(), label('name')),
        timestamp: safeOptional(z.string(), label('timestamp')),
        license_plate: safeOptional(z.string(), label('license_plate')),
        fleet_id: safeOptional(z.string(), label('fleet_id')),
        active_mission_id: safeOptional(z.string(), label('active_mission_id')),
        latest_vehicle_update: safeOptional(z.string(), label('latest_vehicle_update')),
        owners: safeOptional(z.array(z.record(z.string(), z.unknown())), label('owners')),
        ads_state: safeField(AdsStateField, label('ads_state')),
        connectivity_status: safeField(ConnectivityStatusField, label('connectivity_status')),
        driving_state: safeField(DrivingStateField, label('driving_state')),
        driver_mode: safeField(DriverModeField, label('driver_mode')),
        position: safeField(PositionField, label('position')),
        speed_ms: safeField(SpeedMsField, label('speed_ms')),
        mrm_state: safeField(MrmStateField, label('mrm_state')),
        deviations: z
            .array(safeField(DeviationField, label('deviations')))
            .nullish()
            .catch(undefined),
    })
);

export const WireVehicleSummarySchema = z.preprocess(
    sanitizeWire,
    z.object({
        vin: z.string().min(1),
        name: safeOptional(z.string(), label('name')),
        license_plate: safeOptional(z.string(), label('license_plate')),
        connectivity_status: safeField(ConnectivityStatusField, label('connectivity_status'))
            .nullish()
            .catch(undefined),
        fleet_id: safeOptional(z.string(), label('fleet_id')),
        type: safeField(WireVehicleType, label('type')),
        subtype: safeField(WireVehicleSubtype, label('subtype')),
        created_at: safeOptional(z.string(), label('created_at')),
        created_by: safeOptional(z.string(), label('created_by')),
    })
);

const ADS_STATES = ['AUTONOMOUS', 'SHADOW', 'NOT_AVAILABLE', 'UNKNOWN'] as const;
const CONNECTIVITY_STATES = ['ONLINE', 'OFFLINE', 'UNKNOWN'] as const;
const DRIVING_STATES = ['DRIVING', 'IDLE', 'PARKED', 'REVERSING', 'UNKNOWN'] as const;
const DRIVER_MODES = ['IN_SUPERVISED', 'OUT_AUTOMATED', 'UNKNOWN'] as const;
const DEVIATION_SEVERITIES = ['FATAL', 'ERROR', 'WARNING', 'INFO', 'UNKNOWN'] as const;
const DEVIATION_STATES = ['ACTIVE', 'RESOLVED', 'UNKNOWN'] as const;
const MRM_STATES = ['EXECUTING_MRM', 'MRC', 'NONE', 'RELEASING_MRC', 'MRM_FAULTED', 'MRM_ERROR', 'UNKNOWN'] as const;
const VEHICLE_TYPES = ['UNKNOWN', 'DUMMY', 'SIMULATOR', 'TRATON'] as const;
const VEHICLE_SUBTYPES = ['UNKNOWN', 'PHYSICAL_VEHICLE', 'HW_RIG', 'SIMULATED_VEHICLE_WITH_CONNECTIVITY'] as const;

const ADS_STATE_PREFIX = 'AUTONOMOUS_SYSTEM_STATE_';
const CONNECTIVITY_STATE_PREFIX = 'CONNECTIVITY_STATE_';
const DRIVING_STATE_PREFIX = 'DRIVING_STATE_';
const DRIVER_MODE_PREFIX = 'DRIVER_MODE_';
const SEVERITY_PREFIX = 'SEVERITY_';
const DEVIATION_STATE_PREFIX = 'DEVIATION_STATE_';
const MRM_STATE_PREFIX = 'MRM_STATE_';
const toDeviation = (
    raw:
        | {
              id?: string | null;
              description?: string | null;
              is_blocking?: boolean | null;
              severity?: string | null;
              state?: string | null;
              sent_at?: string | null;
          }
        | null
        | undefined
): DeviationFE | undefined => {
    if (raw == null) {
        return;
    }
    const state: DeviationStateFE = fromEnum(raw.state, DEVIATION_STATE_PREFIX, DEVIATION_STATES);
    return {
        id: raw.id ?? '',
        description: raw.description ?? '',
        isBlocking: raw.is_blocking ?? false,
        severity: fromEnum<DeviationSeverityFE>(raw.severity, SEVERITY_PREFIX, DEVIATION_SEVERITIES),
        state,
        sentAt: toDate(raw.sent_at) ?? null,
        isActive: state === 'ACTIVE',
    };
};

const toOwner = (raw: {
    id?: string | null;
    full_name?: string | null;
    email?: string | null;
    phone_number?: string | null;
    position?: string | null;
}): VehicleOwnerFE => ({
    id: raw.id ?? '',
    fullName: raw.full_name ?? '',
    email: raw.email ?? undefined,
    phoneNumber: raw.phone_number ?? undefined,
    position: raw.position ?? undefined,
});

export const vehicleFromRestV2 = (raw: unknown): VehicleFE => {
    /*Double assertion (`as unknown as WireVehicle`) is required because the
    `safeField`/`safeOptional` wrappers replace each field with a
    `catchWithLog(schema.nullish(), fallback)` schema, so `z.infer` of
     WireVehicleSchema no longer structurally matches `VehicleTO`.
     guarded by tests/zodVersionAlignment.test.ts.*/
    const wire = WireVehicleSchema.parse(raw) as unknown as WireVehicle;

    const deviations: MonitoredField<DeviationFE>[] = (wire.deviations ?? []).map(entry =>
        toMonitoredField(
            entry,
            value => toDeviation(value.value),
            value => value.updated_at
        )
    );

    const hasBlockingDeviation = deviations.some(
        field => field.value?.value?.isActive === true && field.value?.value?.isBlocking === true
    );

    return {
        vin: wire.vin,
        name: wire.name ?? '',
        licensePlate: wire.license_plate ?? undefined,
        fleetId: wire.fleet_id ?? undefined,
        activeMissionId: wire.active_mission_id ?? undefined,
        owners: (wire.owners ?? []).map(toOwner),
        latestVehicleUpdate: toDate(wire.latest_vehicle_update) ?? null,
        timestamp: toDate(wire.timestamp) ?? null,
        ads: toMonitoredField(
            wire.ads_state,
            value => fromEnum<AdsStateFE>(value.state, ADS_STATE_PREFIX, ADS_STATES),
            value => value.updated_at
        ),
        connectivity: toMonitoredField<
            NonNullable<NonNullable<typeof wire.connectivity_status>['value']>,
            ConnectivityDetailsFE
        >(
            wire.connectivity_status,
            value => ({
                state: fromEnum(value.state, CONNECTIVITY_STATE_PREFIX, CONNECTIVITY_STATES),
                expectDisconnection: value.expect_disconnection ?? undefined,
                latency: value.latency ?? undefined,
            }),
            value => value.updated_at
        ),
        drivingState: toMonitoredField(
            wire.driving_state,
            value => fromEnum<DrivingStateFE>(value.state, DRIVING_STATE_PREFIX, DRIVING_STATES),
            value => value.updated_at
        ),
        driverMode: toMonitoredField(
            wire.driver_mode,
            value => fromEnum<DriverModeFE>(value.mode, DRIVER_MODE_PREFIX, DRIVER_MODES),
            value => value.updated_at
        ),
        position: toMonitoredField(
            wire.position,
            value => toGeoPosition(value.position),
            value => value.updated_at
        ),
        speedKmh: toMonitoredField(
            wire.speed_ms,
            value => (typeof value.speed_ms === 'number' ? value.speed_ms * 3.6 : undefined),
            value => value.updated_at
        ),
        mrm: toMonitoredField<NonNullable<NonNullable<typeof wire.mrm_state>['value']>, MrmDetailsFE>(
            wire.mrm_state,
            value => ({
                state: fromEnum<MrmStateFE>(value.data?.state, MRM_STATE_PREFIX, MRM_STATES),
                causes: value.data?.causes ?? [],
            }),
            value => value.updated_at
        ),
        deviations,
        hasBlockingDeviation,
    };
};

export const vehicleSummaryFromRestV2 = (raw: unknown): VehicleSummaryFE => {
    const wire = WireVehicleSummarySchema.parse(raw) as WireVehicleSummary;

    const connectivity = toMonitoredField<
        NonNullable<NonNullable<typeof wire.connectivity_status>['value']>,
        ConnectivityDetailsFE
    >(
        wire.connectivity_status,
        value => ({
            state: fromEnum(value.state, CONNECTIVITY_STATE_PREFIX, CONNECTIVITY_STATES),
            expectDisconnection: value.expect_disconnection ?? undefined,
            latency: value.latency ?? undefined,
        }),
        value => value.updated_at
    );

    return {
        vin: wire.vin,
        fleetId: wire.fleet_id ?? undefined,
        name: wire.name ?? wire.vin,
        connectivity: serializeMonitoredField(connectivity),
        type: fromPlainEnum<VehicleTypeFE>(wire.type, VEHICLE_TYPES),
        subtype: fromPlainEnum<VehicleSubtypeFE>(wire.subtype, VEHICLE_SUBTYPES),
        createdAt: wire.created_at ?? undefined,
        licensePlate: wire.license_plate ?? undefined,
        createdBy: wire.created_by ?? undefined,
    };
};
